import { useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { ChevronLeft, Play, Loader2 } from "lucide-react";
import { toTitleCase } from "@/lib/utils";

// Customer-facing video browser: Segment → Instrument → Video.
// Admins upload a per-instrument `video_url` via Admin → Instruments. Supports
// both YouTube links (auto-embedded) and direct .mp4 URLs (HTML5 video).
// Instruments / segments with NO video link are filtered out entirely.
export default function VideosPage() {
  const [allInstruments, setAllInstruments] = useState([]);
  const [segment, setSegment] = useState("");
  const [instrument, setInstrument] = useState(null);
  const [loading, setLoading] = useState(true);

  // One-shot fetch — get every instrument that has a video. We derive segments
  // and per-segment instrument lists from this single list so empty segments
  // never show up in the dropdown.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.get("/instruments/by-segment")
      .then(r => {
        if (cancelled) return;
        const withVideo = (r.data || []).filter(i => !!(i.video_url || "").trim());
        setAllInstruments(withVideo);
      })
      .catch(() => { if (!cancelled) setAllInstruments([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Segments where at least one instrument has a video.
  const segments = useMemo(() => {
    const set = new Set();
    allInstruments.forEach(i => { if (i.segment) set.add(i.segment); });
    return Array.from(set).sort();
  }, [allInstruments]);

  // Instruments for the currently picked segment (already pre-filtered to have video).
  const instruments = useMemo(() => {
    if (!segment) return [];
    return allInstruments.filter(i => i.segment === segment);
  }, [segment, allInstruments]);

  // Convert any YouTube URL into an embeddable URL. Returns null for non-YT.
  const youtubeEmbed = useMemo(() => {
    if (!instrument?.video_url) return null;
    const m = instrument.video_url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  }, [instrument]);

  return (
    <div className="space-y-4" data-testid="videos-page">
      <div className="flex items-center gap-3">
        {instrument && (
          <button onClick={() => setInstrument(null)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="video-back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Videos</h1>
          <p className="text-xs text-gray-500">Pick a segment and an instrument to watch its product video.</p>
        </div>
      </div>

      {!instrument && (
        <>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#008A99] mx-auto" /></div>
          ) : segments.length === 0 ? (
            <p className="text-center text-xs text-gray-400 italic py-10">No videos uploaded yet.</p>
          ) : (
            <>
              {/* Segment dropdown */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Segment</label>
                <select
                  value={segment}
                  onChange={e => setSegment(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 border rounded-xl text-sm bg-white focus:outline-none focus:border-[#008A99]"
                  data-testid="video-segment-select"
                >
                  <option value="">— Choose a segment —</option>
                  {segments.map(s => (
                    <option key={s} value={s}>{toTitleCase(s)}</option>
                  ))}
                </select>
              </div>

              {/* Instrument list */}
              {segment && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Instrument</label>
                  <div className="mt-2 space-y-2" data-testid="video-instrument-list">
                    {instruments.map(inst => (
                      <button
                        key={inst.instrument_code}
                        onClick={() => setInstrument(inst)}
                        className="w-full flex items-center gap-3 p-3 border rounded-xl text-left transition-all bg-white hover:border-[#008A99] hover:shadow-sm cursor-pointer"
                        data-testid={`video-inst-${inst.instrument_code}`}
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-[#E0F4F5] to-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Play className="w-4 h-4 text-[#008A99]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-900 truncate">{toTitleCase(inst.name || inst.instrument_code)}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">Tap to watch</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {instrument && (
        <div className="space-y-3" data-testid="video-player-section">
          <div className="bg-black rounded-xl overflow-hidden aspect-video">
            {youtubeEmbed ? (
              <iframe
                src={youtubeEmbed}
                title={instrument.name}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-youtube-iframe"
              />
            ) : (
              <video
                src={instrument.video_url}
                controls
                playsInline
                className="w-full h-full"
                data-testid="video-mp4-player"
              />
            )}
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Now playing</p>
            <h2 className="text-base font-bold text-gray-900 mt-0.5">{toTitleCase(instrument.name || instrument.instrument_code)}</h2>
            <p className="text-[11px] text-gray-500 mt-1">{toTitleCase(instrument.segment || "")}{instrument.system_type ? ` · ${toTitleCase(instrument.system_type)}` : ""}</p>
            {instrument.description && <p className="text-xs text-gray-700 mt-2 leading-relaxed">{instrument.description}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
